import { useMsal } from '@azure/msal-react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { useUserGroups } from '@sodexo/admin-module-react';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { AppInsightEvent } from '../Models/AppInsight/AppInsightEvent';
import type { AppInsightPage } from '../Models/AppInsight/AppInsightPage';
import { useStore } from '../store/store';
import useCurrentPage from './RouteEffect';

/**
 * Hook to track any event in App Insights.
 * As fetching the groups of the admin module is asynchronous, we built a retry mechanism.
 * If we want to track an event but we haven't received the groups, we retry 3 times,
 * with 5 seconds between each tries.
 */
export function useAppInsightTrackEvent() {
  const [trackToDispatch, setTrackToDispatch] = useState<(() => void)[]>([]);
  const [tries, setTries] = useState(0);
  const { instance } = useMsal();

  const appInsights = useAppInsightsContext();
  const page = useCurrentPage();
  const sdxAdminGroups = useUserGroups(instance.getActiveAccount()?.username || '');
  const [catalog] = useStore(useShallow(state => [state.catalog]));

  useEffect(() => {
    if (trackToDispatch.length > 0) {
      if (sdxAdminGroups.data) {
        dispatchWaitingFunctions();
      } else {
        setTimeout(() => {
          if (tries < 3) {
            if (sdxAdminGroups.data) dispatchWaitingFunctions();
            else setTries(prev => prev + 1);
          } else {
            setTrackToDispatch([]);
            throw new Error("We couldn't fetch user groups and track events");
          }
        }, 5000);
      }
    }
  }, [trackToDispatch]);

  const dispatchWaitingFunctions = () => {
    trackToDispatch.forEach(fn => fn());
    setTrackToDispatch([]);
  };

  const trackEvent = (name: AppInsightEvent, data: object) => {
    const trackData = {
      catalog: catalog!.name,
      taxonomy: catalog!.taxonomy,
      name,
      properties: { page, groups: sdxAdminGroups.data?.map(g => g.name), ...data },
    };
    if (!sdxAdminGroups.data) {
      setTrackToDispatch(prev => {
        return [...prev, () => appInsights.trackEvent(trackData)];
      });
    } else {
      appInsights.trackEvent(trackData);
    }
  };

  return trackEvent;
}

export function useAppInsightTrackPageView(page: AppInsightPage) {
  const { instance } = useMsal();
  const sdxAdminGroups = useUserGroups(instance.getActiveAccount()?.username || '');

  const appInsights = useAppInsightsContext();

  useEffect(() => {
    if (sdxAdminGroups.data) {
      appInsights.trackPageView({
        name: page,
        properties: {
          groups: sdxAdminGroups.data.map(g => g.name),
        },
      });
    }
  }, [sdxAdminGroups.data]);
}
